import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import * as crypto from 'node:crypto';
import { ClassRoom } from '../classroom.js';
import { Teacher } from '../teacher.js';
import { generateJoinCode, isDuplicateKeyError } from '../utils/join-code.js';

const MAX_CODE_GENERATION_ATTEMPTS = 10;

export const createClassroomMutation = registerMutation({
  operationName: 'createClassroomMutation',
  type: () => ClassRoom,
  params: [{ name: 'name', type: () => String }],

  resolve: (name: string) => async (_gqlFields: BaseQLSelectionSet<ClassRoom>) => {
    const currentUser = await CurrentUserInfo.getMyself()({ _id: 1 });
    if (!currentUser?._id) {
      throw new Error('Nicht autorisiert: Kein User eingeloggt');
    }

    const teacherCollection = await getCollection(Teacher);
    const teacher = await teacherCollection.findOne({ userID: currentUser._id });
    if (!teacher) {
      throw new Error('Nicht autorisiert: Kein Lehrerprofil gefunden');
    }

    const trimmedName = typeof name === 'string' ? name.trim() : '';
    if (!trimmedName) {
      throw new Error('Klassenname darf nicht leer sein');
    }

    const classroomCollection = await getCollection(ClassRoom);

    // Relies on the unique index on `code` (see ensure-classroom-indexes.ts) to make this race-free:
    // instead of checking-then-inserting, we insert straight away and only retry on an actual collision.
    let classroom: ClassRoom | undefined;
    for (let attempt = 0; attempt < MAX_CODE_GENERATION_ATTEMPTS; attempt++) {
      const candidate = new ClassRoom({
        _id: crypto.randomUUID(),
        teacherID: teacher._id,
        name: trimmedName,
        code: generateJoinCode(),
        createdAt: new Date().toISOString(),
      });
      try {
        await classroomCollection.insertOne(candidate.toObject());
        classroom = candidate;
        break;
      } catch (err) {
        if (!isDuplicateKeyError(err)) throw err;
      }
    }
    if (!classroom) {
      throw new Error('Failed to generate a unique classroom code after 10 attempts');
    }

    return classroom;
  },
});
